import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider, useTheme } from "next-themes";
import axios from "axios";
import { Toaster, toast } from "sonner";
import "@/App.css";

// i18n
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";

import ContactWidget from "./components/ContactWidget";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import QueryPage from "./pages/QueryPage";
import HistoryPage from "./pages/HistoryPage";
import SharedRecordPage from "./pages/SharedRecordPage";
import AccountPage from "./pages/AccountPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import CheckoutCancelPage from "./pages/CheckoutCancelPage";
import PlatformDashboard from "./pages/PlatformDashboard";
import { FEATURE_PRIVACY_POLICY } from "./config/featureFlags";
import ProfileOnboardingPage from "./pages/ProfileOnboardingPage";
import PeoplePage from "./pages/PeoplePage";
import SubjectProfilePage from "./pages/SubjectProfilePage";
import {
  clinicRouteRedirect,
  CONSUMER_HOME_PATH,
  CLINIC_PRIMARY_PATHS,
  DEMO_MOLECULAR_PATH,
  HEALTH_PROFILE_ONBOARDING_PATH,
  shouldForceHealthProfileOnboarding,
} from "./routing/b2cAccess";
import { SessionProvider } from "./SessionContext";
import { SubjectProvider } from "./SubjectContext";

const API = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Token helpers
const TOKEN_KEY = "mc_access_token";
const REFRESH_KEY = "mc_refresh_token";

function getStoredTokens() {
  return {
    access: localStorage.getItem(TOKEN_KEY),
    refresh: localStorage.getItem(REFRESH_KEY),
  };
}

function storeTokens(access, refresh) {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Axios interceptors — attach JWT, auto-refresh on 401
  useEffect(() => {
    let isRefreshing = false;
    let refreshPromise = null;

    const reqInterceptor = axios.interceptors.request.use((config) => {
      if (config._retry) return config;
      const { access } = getStoredTokens();
      const isShareRequest = config.url?.includes("/api/share/");
      if (access && !isShareRequest) {
        config.headers.Authorization = `Bearer ${access}`;
      }
      return config;
    });

    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        // Don't retry auth endpoints to avoid loops
        const isAuthEndpoint = originalRequest?.url?.includes("/api/auth/login") ||
                               originalRequest?.url?.includes("/api/auth/refresh") ||
                               originalRequest?.url?.includes("/api/auth/signup");
        const isShareEndpoint = originalRequest?.url?.includes("/api/share/");

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthEndpoint &&
          !isShareEndpoint
        ) {
          originalRequest._retry = true;

          // Deduplicate concurrent refresh calls
          if (!isRefreshing) {
            isRefreshing = true;
            const { refresh } = getStoredTokens();
            if (refresh) {
              refreshPromise = axios.post(`${API}/auth/refresh`, { refresh_token: refresh })
                .then((resp) => {
                  const d = resp.data?.data;
                  if (d?.access_token) {
                    storeTokens(d.access_token, d.refresh_token);
                    return d.access_token;
                  }
                  return null;
                })
                .catch(() => null)
                .finally(() => { isRefreshing = false; });
            } else {
              isRefreshing = false;
              refreshPromise = Promise.resolve(null);
            }
          }

          const newToken = await refreshPromise;
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          }

          // Refresh failed — but don't clear tokens on callback page
          if (window.location.pathname !== '/auth/callback') {
            clearTokens();
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

  const fetchBackendUser = useCallback(async () => {
    const { access } = getStoredTokens();
    if (!access) { setUser(null); setLoading(false); return null; }
    try {
      const response = await axios.get(`${API}/auth/me`);
      if (response.data?.data) {
        setUser(response.data.data);
        return response.data.data;
      }
    } catch (err) {
      console.error("Failed to fetch backend user:", err?.response?.status);
      clearTokens();
      setUser(null);
    }
    return null;
  }, []);

  // On mount: check for stored token and fetch user
  // Skip on /auth/callback — that page handles auth itself
  useEffect(() => {
    if (window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    fetchBackendUser().finally(() => setLoading(false));
  }, [fetchBackendUser]);

  const login = (userData, tokens) => {
    if (tokens) storeTokens(tokens.access_token, tokens.refresh_token);
    setUser(userData);
  };

  const logout = async () => {
    const { refresh } = getStoredTokens();
    try {
      await axios.post(`${API}/auth/logout`, { refresh_token: refresh }).catch(() => {});
    } catch (e) { /* ignore */ }
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, fetchBackendUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Applies resolved clinical theme on the layout wrapper (not via always-light .theme-clinical).
const ClinicalThemeWrapper = ({ children }) => {
  const { resolvedTheme, theme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const activeTheme = mounted ? resolvedTheme || theme || "clinical-light" : "clinical-light";

  return (
    <div className="theme-clinical min-h-screen bg-background text-foreground" data-theme={activeTheme}>
      {children}
    </div>
  );
};

// Clinical workspace shell for post-auth workflow routes
const ClinicalLayout = ({ children }) => (
  <ThemeProvider
    attribute="data-theme"
    defaultTheme="clinical-light"
    storageKey="mc-clinical-theme"
    enableSystem={false}
    themes={["clinical-light", "clinical-dark"]}
  >
    <ClinicalThemeWrapper>{children}</ClinicalThemeWrapper>
  </ThemeProvider>
);

const ConsumerHomeRedirect = () => (
  <Navigate to={CONSUMER_HOME_PATH} replace />
);

// Protected Route — B2C: authenticated users may proceed without org_id
const ProtectedRoute = ({ children, clinical = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    const loadingShell = (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div
            className={`w-8 h-8 border-2 rounded-full animate-spin ${
              clinical
                ? "border-muted border-t-foreground"
                : "border-primary/30 border-t-primary"
            }`}
          />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
    return clinical ? <ClinicalLayout>{loadingShell}</ClinicalLayout> : loadingShell;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const clinicHome = clinicRouteRedirect(location.pathname, user);
  if (clinicHome) {
    return <Navigate to={clinicHome} replace />;
  }

  if (shouldForceHealthProfileOnboarding(user, location.pathname)) {
    return <Navigate to={HEALTH_PROFILE_ONBOARDING_PATH} replace />;
  }

  return clinical ? <ClinicalLayout>{children}</ClinicalLayout> : children;
};

/** Public `/` — login for guests; consumer home when authenticated. */
const PublicHomeRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    if (shouldForceHealthProfileOnboarding(user, "/")) {
      return <Navigate to={HEALTH_PROFILE_ONBOARDING_PATH} replace />;
    }
    return <Navigate to={CONSUMER_HOME_PATH} replace />;
  }

  return <Navigate to="/login" replace />;
};

// Main Router
function AppRouter() {
  const { language } = useLanguage();
  return (
    <>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/privacy"
        element={FEATURE_PRIVACY_POLICY ? <PrivacyPolicyPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/terms"
        element={FEATURE_PRIVACY_POLICY ? <TermsOfUsePage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/cookies"
        element={FEATURE_PRIVACY_POLICY ? <CookiePolicyPage /> : <Navigate to="/login" replace />}
      />
      {CLINIC_PRIMARY_PATHS.map((path) => (
        <Route key={path} path={path} element={<ConsumerHomeRedirect />} />
      ))}
      <Route path={DEMO_MOLECULAR_PATH} element={<ConsumerHomeRedirect />} />
      <Route path="/" element={<PublicHomeRoute />} />
      <Route path={HEALTH_PROFILE_ONBOARDING_PATH} element={
        <ProtectedRoute clinical>
          <ProfileOnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/case/new" element={
        <ProtectedRoute clinical>
          <QueryPage />
        </ProtectedRoute>
      } />
      <Route path="/cases" element={
        <ProtectedRoute clinical>
          <HistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/history" element={<Navigate to="/cases" replace />} />
      <Route path="/people" element={
        <ProtectedRoute clinical>
          <PeoplePage />
        </ProtectedRoute>
      } />
      <Route path="/people/:subjectId" element={
        <ProtectedRoute clinical>
          <SubjectProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute clinical>
          <AccountPage />
        </ProtectedRoute>
      } />
      <Route path="/platform" element={
        <ProtectedRoute clinical>
          <PlatformDashboard />
        </ProtectedRoute>
      } />
      <Route path="/checkout/success" element={
        <ProtectedRoute clinical>
          <CheckoutSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/checkout/cancel" element={
        <ProtectedRoute clinical>
          <CheckoutCancelPage />
        </ProtectedRoute>
      } />
      <Route path="/share/:token" element={<SharedRecordPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <ContactWidget lang={language} apiEndpoint={`${API}/contact`} />
    </>
  );
}

function SubjectGate({ children }) {
  const { user } = useAuth();
  return <SubjectProvider user={user}>{children}</SubjectProvider>;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <LanguageProvider>
          <AuthProvider>
            <SessionProvider>
              <SubjectGate>
                <AppRouter />
              </SubjectGate>
            </SessionProvider>
            <Toaster position="bottom-right" richColors />
          </AuthProvider>
        </LanguageProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
